/**
 * Google Analytics 4 — gated by cookie consent.
 *
 * GA4 only loads after the user explicitly clicks "Accept all" in the
 * cookie banner. Choosing "Essential only", "Decline", or dismissing the
 * banner keeps analytics disabled.
 *
 * Usage:
 *   import { initAnalytics, track, trackPageView, resetConsent } from "@/lib/analytics";
 *   initAnalytics();              // call once at app start; safe to call again
 *   track("contact_submit");      // fire a custom event
 *   trackPageView("/blog");       // fire a page_view manually for SPA route changes
 *   resetConsent();               // clear stored choice so the banner reappears
 */

const CONSENT_KEY = "ksdevarc_cookie_consent";
const GA_ID = process.env.REACT_APP_GA_ID || "";
const EVENT_NAME = "ksdevarc:consent-changed";

let loaded = false;

export function hasAnalyticsConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const { choice } = JSON.parse(raw);
    return choice === "accepted";
  } catch {
    return false;
  }
}

function loadGtagScript() {
  if (loaded || !GA_ID || typeof document === "undefined") return;
  loaded = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID, {
    anonymize_ip: true,
    send_page_view: true,
  });
}

export function initAnalytics() {
  if (hasAnalyticsConsent()) loadGtagScript();

  // Listen for consent changes (fired by CookieConsent when user clicks Accept all)
  if (typeof window !== "undefined" && !window.__ksdevarcAnalyticsListener) {
    window.__ksdevarcAnalyticsListener = true;
    window.addEventListener(EVENT_NAME, () => {
      if (hasAnalyticsConsent()) loadGtagScript();
    });
  }
}

export function track(event, params = {}) {
  if (!hasAnalyticsConsent() || typeof window.gtag !== "function") return;
  try {
    window.gtag("event", event, params);
  } catch {
    // best-effort
  }
}

export function trackPageView(path) {
  if (!hasAnalyticsConsent() || typeof window.gtag !== "function") return;
  try {
    window.gtag("event", "page_view", {
      page_path: path || window.location.pathname + window.location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  } catch {
    // best-effort
  }
}

export function notifyConsentChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(EVENT_NAME));
}

export function resetConsent() {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    // ignore
  }
  notifyConsentChanged();
  // Force a reload so any in-memory GA state is wiped between sessions
  if (typeof window !== "undefined") window.location.reload();
}
